import { brandLogoForChannel } from 'client-portal/utils/channel-brand';
import { consentDocsForUrls } from 'client-portal/utils/transfer-consent-docs';
import { tracked } from '@glimmer/tracking';
import ClientSwitchHelper, {
  notifyBugsnag,
} from 'client-portal/utils/billing-transfer/client-switch';
import Service, { service } from '@ember/service';

const BILLING_ROUTE = 'site.billing';
const TRANSFER_ROUTE = 'site.billing.transfer';
const HOME_ROUTE = 'site.home';
const TRANSFER_PATH = 'billing/transfer';
const PENDING_STATUSES = ['started', 'in_progress'];
const MODAL_DISMISSED_KEY = 'billing-transfer-modal-dismissed';
const AWAITING_PAYMENT_KEY = 'billing-transfer-awaiting-payment';
const PAYMENT_TAB_POLL_MS = 5000;
const PAYMENT_TAB_POLL_MAX_HITS = 60;
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
const ERROR_TRANSLATION_KEYS = {
  'guardian_required': 'billing.transferSetup.errors.guardianRequired',
  'validation_failed': 'billing.transferSetup.errors.validationFailed',
  'enrollment_not_pending': 'billing.transferSetup.errors.enrollmentNotPending',
};

export default class BillingTransferService extends Service {
  @service analytics;
  @service session;
  @service store;
  @service intl;
  @service practiceStorage;
  @service fastboot;
  @service router;
  @service announcement;

  @tracked enrollmentStatus = null;
  @tracked targetClientId = null;
  @tracked brandName = null;
  @tracked brandLogo = null;
  @tracked channelName = null;
  @tracked consentDocumentUrls = null;
  @tracked isModalOpen = false;
  @tracked errorMessage = null;
  @tracked consentError = false;
  @tracked consentAccepted = false;
  @tracked consentClientId = null;
  @tracked billingPortalUrl = null;
  @tracked isSubmitting = false;
  @tracked awaitingPayment = false;

  // Session bookkeeping — not template-bound.
  autoShownClientId = null;

  #paymentTabPollId = null;
  #paymentTabCloseResolve = null;
  #paymentTabSyncGeneration = 0;
  #syncInFlight = false;
  #clientSwitch;

  constructor() {
    super(...arguments);
    this.#clientSwitch = new ClientSwitchHelper(this);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.#stopBackgroundWork();
  }

  // Aborts an in-flight payment-tab poll (also used by unit afterEach cleanup).
  cancelPaymentTabWatch = () => {
    this.#stopBackgroundWork();
  };

  // The post-transfer partner banner reads the client's channel enrollment, which only the
  // billing route model loads (with its full fields/includes). Re-run it rather than reloading
  // the client here with a narrower query.
  refreshBillingRoute = async () => {
    if (this.fastboot.isFastBoot || !this.router.currentRouteName?.startsWith(BILLING_ROUTE)) {
      return;
    }

    try {
      await this.router.refresh(BILLING_ROUTE);
    } catch (error) {
      notifyBugsnag(error);
    }
  };

  // Every billing-transfer event is attributed to the enrollment's channel.
  track(eventName, properties) {
    this.analytics.selectiveTrack(eventName, { channel: this.channelName, ...properties });
  }

  get isTargetClientCurrentClient() {
    return this.#clientSwitch.isCurrent(this.targetClientId);
  }

  get isPendingTransfer() {
    return PENDING_STATUSES.includes(this.enrollmentStatus);
  }

  get isCompleted() {
    return this.enrollmentStatus === 'completed';
  }

  get isAwaitingPayment() {
    return this.awaitingPayment || this.#isAwaitingPaymentActive();
  }

  // Consent is latched against the client it was recorded for, so a switch to another client
  // drops it even when that client's load fails or early-returns before the latch is rewritten.
  get consentOnRecord() {
    return Boolean(this.consentClientId) && this.consentClientId === this.targetClientId;
  }

  get hasAutoShownThisSession() {
    let clientId = this.targetClientId;

    return Boolean(clientId && this.autoShownClientId === clientId);
  }

  get shouldShowModalOnLoad() {
    return (
      this.isPendingTransfer &&
      !this.#isModalDismissalActive() &&
      !this.isAwaitingPayment &&
      !this.hasAutoShownThisSession
    );
  }

  get showBanner() {
    return this.isPendingTransfer && !this.isModalOpen;
  }

  get consentDocs() {
    return consentDocsForUrls(this.consentDocumentUrls);
  }

  // Wired via {{on-document-visibility}} in TransferSetupModalHost (browser DOM only).
  onDocumentVisible = () => {
    if (!this.isPendingTransfer || (this.isAwaitingPayment && this.consentOnRecord)) {
      this.#syncEnrollment();
      return;
    }

    this.load({ reload: true });
  };

  async load({ clientId, channelName } = {}) {
    try {
      await this.#loadEnrollment({ clientId, channelName });
      return true;
    } catch (error) {
      notifyBugsnag(error);
      return false;
    }
  }

  async #loadEnrollment({ clientId, channelName } = {}) {
    if (!this.session.currentClient) {
      this.targetClientId = null;
      this.#clearEnrollmentState();
      return;
    }

    if (clientId) {
      this.targetClientId = clientId;
    }

    if (!this.isTargetClientCurrentClient) {
      return;
    }

    let adapter = this.store.adapterFor('billing-transfer-setup');
    let response = await adapter.fetch(
      this.#channelRequestParams({ clientId: clientId ?? this.targetClientId, channelName })
    );

    this.targetClientId = response?.clientId ?? null;

    if (!response?.enrollmentStatus) {
      this.#clearEnrollmentState();
      return;
    }

    this.consentDocumentUrls = response.consentDocuments?.urls ?? null;
    this.#applyEnrollment(response);
    this.#maybeResumeAwaitingSync();
  }

  switchToTargetClient() {
    return this.#clientSwitch.switchTo(this.targetClientId);
  }

  takeClientSwitchRetry() {
    return this.#clientSwitch.takeRetry(this.targetClientId);
  }

  // Synchronous so callers running before a redirect can skip awaiting a switch that
  // cannot happen; an async yield there stops the router from following the redirect.
  canSwitchForTransferDeepLink(transition) {
    return this.#clientSwitch.canSwitchFor(transition, TRANSFER_ROUTE, TRANSFER_PATH);
  }

  async switchIfTransferClientMismatch(transition) {
    try {
      let clientId = this.#clientSwitch.transferClientId(transition, TRANSFER_ROUTE, TRANSFER_PATH);
      if (!clientId) {
        return false;
      }

      this.targetClientId = clientId;
      return await this.switchToTargetClient();
    } catch (error) {
      notifyBugsnag(error);
      return false;
    }
  }

  resetBillingTransferSession(transition) {
    if (
      transition?.to?.name === HOME_ROUTE ||
      !this.#clientSwitch.hasTransferRedirect(TRANSFER_PATH)
    ) {
      return;
    }

    this.session.set('data.redirect', undefined);
  }

  async loadAfterBillingModel(transition) {
    try {
      await this.#loadEnrollmentAfterBilling(transition);
    } catch (error) {
      notifyBugsnag(error);
    }
  }

  async #loadEnrollmentAfterBilling(transition) {
    if (this.fastboot.isFastBoot || transition?.to?.name === TRANSFER_ROUTE) {
      return;
    }

    this.targetClientId = this.#clientSwitch.alignedTargetId(
      this.session.currentClient?.hashedId,
      this.targetClientId
    );
    await this.load();

    if (this.shouldShowModalOnLoad) {
      this.openModal();
    }
  }

  #maybeResumeAwaitingSync() {
    if (this.isAwaitingPayment && this.isPendingTransfer && !this.#syncInFlight) {
      // Fire-and-forget: resume sync after a refresh while still awaiting payment.
      this.#syncEnrollment();
    }
  }

  openModal = options => {
    let force = options?.force === true;

    if (!this.isPendingTransfer && !force) {
      return;
    }

    if (force) {
      this.#clearModalDismissal();
    }

    // Once auto-shown (or opened at all) for this client this session, never auto-respawn.
    this.#markAutoShownThisSession();
    this.isModalOpen = true;
    this.errorMessage = null;
    this.consentError = false;
    this.billingPortalUrl = null;
    this.consentAccepted = false;
  };

  closeModal = () => {
    this.#resetModal();
    this.#markAutoShownThisSession();
    this.#persistModalDismissal();
  };

  toggleConsentAccepted = ({ checked }) => {
    this.consentAccepted = checked;

    if (this.consentAccepted) {
      this.consentError = false;
    }
  };

  addPayment = async () => {
    if (!this.consentAccepted) {
      this.consentError = true;
      return;
    }

    this.consentError = false;
    this.errorMessage = null;
    this.billingPortalUrl = null;
    this.isSubmitting = true;
    // Abort any prior tab watcher; bump generation so its sync is skipped.
    this.#stopBackgroundWork();

    let paymentTab = window.open('', '_blank');

    try {
      let adapter = this.store.adapterFor('billing-transfer-setup');
      let response = await adapter.submit({
        consentAccepted: true,
        ...this.#channelRequestParams(),
      });
      this.#recordConsent(true);

      let billingPortalUrl = response.billingPortalUrl;

      if (!billingPortalUrl) {
        paymentTab?.close();
        this.errorMessage = this.intl.t('billing.transferSetup.errors.contactProvider');
        return;
      }

      if (!paymentTab) {
        this.billingPortalUrl = billingPortalUrl;
        this.errorMessage = this.intl.t('billing.transferSetup.errors.popupBlocked');
        return;
      }

      // The create response only carries the enrollment status, so take that rather than
      // assuming `in_progress`: the partner can report a payment method already on file, and
      // the backend then settles the row on this same request with nothing left to pay.
      this.enrollmentStatus = response.enrollmentStatus ?? 'in_progress';

      if (!this.isPendingTransfer) {
        paymentTab?.close();
        this.#resetCompletedTransfer();
        // The partner banner reads the client's enrollment off the billing route model, so a
        // transfer that settles on this request needs the same refresh the sync path does.
        await this.refreshBillingRoute();
        return;
      }

      paymentTab.location = billingPortalUrl;
      this.#writeAwaitingPayment(true);
      this.closeModal();

      this.#watchPaymentTabAndSync(paymentTab);
    } catch (error) {
      paymentTab?.close();

      await this.load({ reload: true });

      if (!this.isPendingTransfer) {
        this.#resetModal();
        return;
      }

      this.errorMessage =
        this.#extractErrorMessage(error) ||
        this.intl.t('billing.transferSetup.errors.contactProvider');
    } finally {
      this.isSubmitting = false;
    }
  };

  async #watchPaymentTabAndSync(paymentTab) {
    let generation = ++this.#paymentTabSyncGeneration;

    await this.#waitForPaymentTabClose(paymentTab);

    if (generation !== this.#paymentTabSyncGeneration) {
      return;
    }

    await this.#syncEnrollment();
  }

  async #syncEnrollment() {
    if (
      !this.consentOnRecord ||
      !this.isAwaitingPayment ||
      !this.isPendingTransfer ||
      this.#syncInFlight
    ) {
      return;
    }

    let generation = this.#paymentTabSyncGeneration;
    this.#syncInFlight = true;

    try {
      try {
        let adapter = this.store.adapterFor('billing-transfer-setup');
        await adapter.sync(this.#channelRequestParams());
      } catch {
        // Best-effort probe; enrollment reload below is the source of truth.
      }

      if (this.#isBackgroundWorkStale(generation)) {
        return;
      }

      await this.load({ reload: true });

      if (this.#isBackgroundWorkStale(generation) || this.isPendingTransfer) {
        return;
      }

      this.#stopBackgroundWork();
      this.#writeAwaitingPayment(false);

      if (this.isCompleted) {
        await this.refreshBillingRoute();
      }
    } finally {
      this.#syncInFlight = false;
    }
  }

  #isBackgroundWorkStale(generation) {
    return this.#isTornDown || generation !== this.#paymentTabSyncGeneration;
  }

  get #isTornDown() {
    return this.isDestroying || this.isDestroyed;
  }

  #waitForPaymentTabClose(paymentTab) {
    return new Promise(resolve => {
      this.#stopPaymentTabPoll();

      if (!paymentTab || paymentTab.closed) {
        resolve();
        return;
      }

      this.#paymentTabCloseResolve = resolve;
      let hits = 0;

      this.#paymentTabPollId = setInterval(() => {
        hits += 1;

        if (!paymentTab || paymentTab.closed || hits >= PAYMENT_TAB_POLL_MAX_HITS) {
          this.#stopPaymentTabPoll();
        }
      }, PAYMENT_TAB_POLL_MS);
    });
  }

  #stopPaymentTabPoll() {
    if (this.#paymentTabPollId !== null) {
      clearInterval(this.#paymentTabPollId);
      this.#paymentTabPollId = null;
    }

    if (this.#paymentTabCloseResolve) {
      let resolve = this.#paymentTabCloseResolve;
      this.#paymentTabCloseResolve = null;
      resolve();
    }
  }

  #stopBackgroundWork() {
    this.#paymentTabSyncGeneration++;
    this.#stopPaymentTabPoll();
  }

  #applyEnrollment(response) {
    let channelName = response.channelName;

    this.enrollmentStatus = response.enrollmentStatus;
    this.channelName = channelName || null;

    this.#applyConsentOnRecord(response);

    // The API names the partner brand ("Evernorth Behavioral Care Group"); the channel's
    // own display name is the fallback for channels without a distinct billing brand.
    this.brandName = response.brandName || response.channelDisplayName || null;
    this.brandLogo = brandLogoForChannel(channelName);
    this.awaitingPayment = this.#isAwaitingPaymentActive();

    if (this.isCompleted || this.enrollmentStatus === 'rejected') {
      this.#resetCompletedTransfer();
      return;
    }

    if (!this.isPendingTransfer) {
      this.#resetModal();
      this.#writeAwaitingPayment(false);
      this.#stopBackgroundWork();
    }
  }

  // Within one client a GET issued before `addPayment` can still land afterwards and must not
  // clear the latch.
  #applyConsentOnRecord(response) {
    if (!this.consentOnRecord && response.consentAccepted !== undefined) {
      this.#recordConsent(response.consentAccepted === true);
    }
  }

  #recordConsent(onRecord) {
    this.consentClientId = onRecord ? this.targetClientId : null;
  }

  #resetModal() {
    this.isModalOpen = false;
    this.consentAccepted = false;
    this.consentError = false;
    this.errorMessage = null;
    this.billingPortalUrl = null;
  }

  #resetCompletedTransfer() {
    this.#resetModal();
    this.#clearModalDismissal();
    this.#writeAwaitingPayment(false);
    this.#stopPaymentTabPoll();
  }

  #clearEnrollmentState() {
    this.#resetModal();
    this.enrollmentStatus = null;
    this.brandName = null;
    this.brandLogo = null;
    this.channelName = null;
    this.consentDocumentUrls = null;
    this.consentClientId = null;
    this.#writeAwaitingPayment(false);
    this.#stopBackgroundWork();
  }

  #markAutoShownThisSession() {
    this.autoShownClientId = this.targetClientId ?? null;
  }

  #writeAwaitingPayment(active) {
    this.awaitingPayment = active;

    if (this.#isTornDown) {
      return;
    }

    let key = this.#awaitingKey;

    if (!key) {
      return;
    }

    if (active) {
      this.#persistTimestamp(key);
    } else {
      this.practiceStorage.remove(key);
    }
  }

  #isAwaitingPaymentActive() {
    return this.#isTimestampActive(this.#awaitingKey);
  }

  #persistModalDismissal() {
    let key = this.#dismissKey;

    if (!key) {
      return;
    }

    this.#persistTimestamp(key);
  }

  #clearModalDismissal() {
    let key = this.#dismissKey;

    if (!key) {
      return;
    }

    this.practiceStorage.remove(key);
  }

  #isModalDismissalActive() {
    return this.#isTimestampActive(this.#dismissKey);
  }

  get #dismissKey() {
    let clientId = this.targetClientId;

    return clientId ? `${MODAL_DISMISSED_KEY}:${clientId}` : null;
  }

  get #awaitingKey() {
    let clientId = this.targetClientId;

    return clientId ? `${AWAITING_PAYMENT_KEY}:${clientId}` : null;
  }

  // A deep link names the channel before any enrollment is loaded, and it may be a
  // subchannel name (`octave_51f285`) that the API resolves to its channel.
  #channelRequestParams({ clientId = this.targetClientId, channelName } = {}) {
    let requestChannelName = channelName || this.channelName;

    return {
      ...(clientId ? { clientId } : {}),
      ...(requestChannelName ? { channelName: requestChannelName } : {}),
    };
  }

  #persistTimestamp(key) {
    this.practiceStorage.set(key, String(Date.now()));
  }

  #isTimestampActive(key) {
    let storedAt = Number(key && this.practiceStorage.get(key));

    return Number.isFinite(storedAt) && Date.now() - storedAt < STORAGE_TTL_MS;
  }

  #extractErrorMessage(error) {
    let code = error?.errors?.[0]?.code || error?.payload?.errorCode;
    let key = ERROR_TRANSLATION_KEYS[code];

    return key ? this.intl.t(key) : null;
  }
}
